<script lang="ts">
	import Button, {
		type Props as ButtonProps
	} from "@components/inputs/Button.svelte";
	import { ApplicationLocation, ApplicationMethod } from "@lib/analyticsTypes";
	import { clarityFireApplicationEvent } from "@lib/clarityEvents";
	import { fetchDistinctId } from "@lib/posthogEvents";
	import { algoliaFireUserAppliedEvent } from "@stores/algolia";
	import { getUser } from "@stores/user.ts";
	import Loading from "@svg/Loading.svelte";
	import { actions } from "astro:actions";
	import { navigate } from "astro:transitions/client";
	import type { UUID } from "crypto";
	import type { Snippet } from "svelte";
	import { toast } from "svelte-sonner";
	import { addUtmParameters } from "../../utils/addUtmParameters";

	interface Props {
		loadingText?: string;
		job: {
			title: string;
			company_uuid: UUID | string;
			job_uuid: UUID | string;
			path?: string;
			jobUrl?: string;
		};
		fireAfterSearchEvent?: boolean;
		applicationLocation: ApplicationLocation;
		buttonProps?: ButtonProps;
		children?: Snippet;
	}

	const {
		loadingText,
		job,
		fireAfterSearchEvent,
		applicationLocation,
		buttonProps,
		children
	}: Props = $props();

	let loadingApply = $state(false);

	export async function directApply() {
		const user = await getUser();

		if (user) {
			loadingApply = true;

			if (!user.email) {
				loadingApply = false;
				return;
			}

			// Create application data object
			const applicationData = {
				job_title: job.title,
				company_uuid: job.company_uuid,
				job_uuid: job.job_uuid,
				name: user.user_metadata.firstName + " " + user.user_metadata.lastName,
				email: user.email,
				phone: user.user_metadata.phone,
				user_uuid: user.id,
				application_location: applicationLocation,
				application_method: ApplicationMethod.DirectApply
			};

			// Ensure that the user has an email or phone number
			if (user.email || user.user_metadata.phone) {
				try {
					// Use the new method to apply for the job and destructure the response
					const { error } =
						await actions.application.applyForJob(applicationData);

					if (!error) {
						algoliaFireUserAppliedEvent({
							objectID: job.job_uuid,
							afterSearch: fireAfterSearchEvent
						});

						clarityFireApplicationEvent(
							job.job_uuid,
							job.company_uuid,
							ApplicationMethod.FormSubmission,
							ApplicationLocation.CompanyPage
						);

						toast.success("Sollicitatie verstuurd!");
					} else {
						toast.error("Er is iets fout gegaan.");
					}
				} catch (exception) {
					toast.error("Er is iets fout gegaan.");
				}

				loadingApply = false;
				return;
			}
		}

		if (window.location.pathname === "/") {
			history.pushState(
				`/?scrollTo=${job.job_uuid}&preventSearch=true`,
				"",
				`/?scrollTo=${job.job_uuid}`
			);
			loadingApply = false;
		}

		// job.path is needed for navigation
		if (job.path) {
			navigate(`/fanpages/${job.path}?scrollTo=contact&preventSearch=true`, {
				history: "replace"
			});
		} else {
			navigate("#contact", {
				history: "replace"
			});
		}

		loadingApply = false;
	}
</script>

{#if job.jobUrl}
	<Button
		target="_blank"
		href={addUtmParameters(job.jobUrl)}
		onclick={(event) => {
			event.preventDefault();
			//prevent event bubbling up
			event.stopPropagation();
			algoliaFireUserAppliedEvent({
				objectID: job.job_uuid,
				afterSearch: fireAfterSearchEvent
			});
			// Fetch the distinct_id of the current PostHog user.
			const distinct_id = fetchDistinctId();
			if (distinct_id) {
				try {
					actions.analytics.captureApplicationEvent({
						distinct_id,
						job_uuid: job.job_uuid as UUID,
						company_uuid: job.company_uuid as UUID,
						application_method: ApplicationMethod.Redirect,
						application_location: applicationLocation
					});
				} catch (e) {
					console.error(e);
				}
			}

			clarityFireApplicationEvent(
				job.job_uuid,
				job.company_uuid,
				ApplicationMethod.FormSubmission,
				ApplicationLocation.CompanyPage
			);

			window.open(
				addUtmParameters(job.jobUrl),
				"_blank",
				"noopener,noreferrer"
			);
		}}
		color="text-emerald-500"
		{...buttonProps}
	>
		{#if loadingApply}
			<Loading class="w-6 animate-spin fill-current" />
		{:else}
			{@render children?.()}
		{/if}
	</Button>
{:else}
	<Button
		{...buttonProps}
		color="text-emerald-500"
		onclick={async (event) => {
			event.stopPropagation();
			event.preventDefault();
			if (loadingApply) {
				return;
			}

			directApply();
		}}
	>
		{#if loadingApply}
			<Loading class="w-6 animate-spin fill-current" />
			{#if loadingText}
				<span class="ml-3">{loadingText}</span>
			{/if}
		{:else}
			{@render children?.()}
		{/if}
	</Button>
{/if}
